import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthorizeService } from 'src/api-authorization/authorize.service';

/** Broadcast by the Account settings page (wwwroot/js/profile-photo.js) after a save or removal. */
export const PROFILE_PHOTO_CHANNEL = 'lrm-profile-photo';

type Versions = Record<string, string>;

interface LoadedPhoto {
  version: string;
  url$: Observable<SafeUrl | null>;
  objectUrl?: string;
}

/**
 * Profile photos for avatars (top bar, Admin › Users, Admin › Usage).
 *
 * Asks once for who has a photo (`GET /api/ProfilePhoto/Versions`, no images), then fetches only
 * real photos, each at a versioned URL the browser caches as immutable. A list of 40 people with 5
 * photos costs one small request plus 5 cached images; nobody without a photo is requested.
 * Images come through HttpClient (so the bearer token is sent) and are shown as object URLs.
 *
 * When the photo changes on the Account settings page, that page broadcasts it and every open tab
 * refreshes the versions, so the new photo appears without a reload.
 */
@Injectable({ providedIn: 'root' })
export class ProfilePhotoService implements OnDestroy {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  private readonly photos = new Map<string, LoadedPhoto>();
  private readonly versions$: Observable<Versions>;
  private channel?: BroadcastChannel;
  private readonly onStorage = (e: StorageEvent) => {
    if (e.key === PROFILE_PHOTO_CHANNEL) {
      this.zone.run(() => this.refresh());
    }
  };

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    authorize: AuthorizeService,
  ) {
    const signedIn$ = authorize.getUser().pipe(map(u => !!u), distinctUntilChanged());

    this.versions$ = combineLatest([signedIn$, this.refresh$]).pipe(
      switchMap(([signedIn]) => signedIn
        ? this.http.get<Versions>('/api/ProfilePhoto/Versions').pipe(catchError(() => of({} as Versions)))
        : of({} as Versions)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    try {
      this.channel = new BroadcastChannel(PROFILE_PHOTO_CHANNEL);
      this.channel.onmessage = () => this.zone.run(() => this.refresh());
    } catch {
      // No BroadcastChannel: the storage event below still works.
    }
    window.addEventListener('storage', this.onStorage);
  }

  /** The user's photo as an image URL, or null when they have none (show initials). */
  photoUrl(userId: string | null | undefined): Observable<SafeUrl | null> {
    if (!userId) {
      return of(null);
    }

    return this.versions$.pipe(
      map(versions => versions[userId] ?? null),
      distinctUntilChanged(),
      switchMap(version => version ? this.load(userId, version) : of(null)),
    );
  }

  /** Re-reads who has a photo (after a change in another tab). */
  refresh(): void {
    this.refresh$.next();
  }

  ngOnDestroy(): void {
    this.channel?.close();
    window.removeEventListener('storage', this.onStorage);
    this.photos.forEach(p => p.objectUrl && URL.revokeObjectURL(p.objectUrl));
  }

  private load(userId: string, version: string): Observable<SafeUrl | null> {
    const cached = this.photos.get(userId);
    if (cached && cached.version === version) {
      return cached.url$;
    }

    // A new version replaces the old one; free the old image.
    if (cached?.objectUrl) {
      URL.revokeObjectURL(cached.objectUrl);
    }

    const entry: LoadedPhoto = { version, url$: of(null) };
    entry.url$ = this.http
      .get(`/api/ProfilePhoto/${encodeURIComponent(userId)}`, { params: { v: version }, responseType: 'blob' })
      .pipe(
        map(blob => {
          entry.objectUrl = URL.createObjectURL(blob);
          // Our own blob: URL; Angular 14's sanitizer would otherwise prefix it with "unsafe:".
          return this.sanitizer.bypassSecurityTrustUrl(entry.objectUrl);
        }),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.photos.set(userId, entry);
    return entry.url$;
  }
}
